/** @format */

import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useMemo } from "react";
import { z } from "zod";

import type { LightningStrike } from "@/api/weatherRadarApi";
import type { ForecastWind, SurfaceWind } from "@/pages/Run/widgets/WeatherRadar";
import InlineErrorBoundary from "@/utils/InlineErrorBoundary";
import Loading from "@/utils/Loading";
import { lazyWithRetry } from "@/utils/lazyWithRetry";

// Lazy-load so Leaflet (excluded from optimizeDeps for code splitting) only
// loads when this route renders — mirrors the MapLazy pattern used elsewhere.
const WeatherRadar = lazyWithRetry(() => import("@/pages/Run/widgets/WeatherRadar"));

const CHARLOTTE_MOTOR_SPEEDWAY = { lat: 35.3528, lng: -80.6826 };

const searchSchema = z.object({
  mock: z.enum(["lightning"]).optional(),
});

// Available in all environments (not just DEV) so the screenshot capture
// can hit it on stage/prod for the docs. The route renders mock-data
// overlays on a public map — nothing to gate. Once the radar feature
// rolls out widely the dev route can be deleted and the screenshot can
// point at a real run page.
export const Route = createFileRoute("/dev/weather-radar")({
  validateSearch: searchSchema,
  component: WeatherRadarPreview,
});

/**
 * Synthetic strike generator for the dev preview. Simulates a storm cell
 * MOVING from SW → NE: older cluster (5–15 min ago) sits ~10 mi SW of the
 * track; recent cluster (<5 min) sits ~6 mi NW. The intentional displacement
 * exercises `computeStormVector` (which needs ≥5 strikes in each time bin)
 * and the closest-strike alert (recent cluster is inside the 10 mi window).
 */
function generateMockStrikes(opts: {
  centerLat: number;
  centerLng: number;
  count: number;
  maxAgeS: number;
}): LightningStrike[] {
  const { centerLat, centerLng, count } = opts;
  const miPerLatDeg = 69;
  const miPerLngDeg = 69 * Math.cos((centerLat * Math.PI) / 180);

  // Mulberry32 PRNG seeded from a constant so HMR doesn't reshuffle.
  let seed = 0xc01a;
  const rand = () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const offsetFromTrack = (offsetMi: number, bearingDeg: number) => {
    const rad = (bearingDeg * Math.PI) / 180;
    return {
      lat: centerLat + (offsetMi * Math.cos(rad)) / miPerLatDeg,
      lng: centerLng + (offsetMi * Math.sin(rad)) / miPerLngDeg,
    };
  };

  // Cell moves W → E directly toward the track. Older cluster sits 14 mi
  // due W (ages 6–14 min); recent cluster sits 9 mi due W (ages 0.5–4 min).
  // ~5 mi displacement over an ~8 min mean-time gap = ~37 mph, with the
  // recent centroid projected to enter the 8 mi alert ring inside the ETA
  // window — exercises the "headed" alert state of the storm vector.
  const olderCenter = offsetFromTrack(14, 270);
  const recentCenter = offsetFromTrack(9, 270);

  const now = Date.now() / 1000;
  const strikes: LightningStrike[] = [];
  // Split: 40% older bin, 60% recent bin (skew toward fresh so the eye reads
  // recent activity strongly and the storm vector arrow is well-supported).
  const olderCount = Math.max(8, Math.floor(count * 0.4));
  const recentCount = count - olderCount;

  const emit = (
    n: number,
    base: { lat: number; lng: number },
    ageRange: [number, number],
    radiusRange: [number, number],
    idOffset: number
  ) => {
    for (let i = 0; i < n; i++) {
      const r = radiusRange[0] + rand() * (radiusRange[1] - radiusRange[0]);
      const theta = rand() * 2 * Math.PI;
      const dLat = (r * Math.cos(theta)) / miPerLatDeg;
      const dLng = (r * Math.sin(theta)) / miPerLngDeg;
      const ageS = ageRange[0] + rand() * (ageRange[1] - ageRange[0]);
      strikes.push({
        id: idOffset + i,
        time: now - ageS,
        lat: base.lat + dLat,
        lng: base.lng + dLng,
        // Spread across GLM's typical 1e-15..1e-9 J range so the energy-
        // weighted marker size differs visibly between strikes.
        energy: 10 ** (-15 + rand() * 6),
      });
    }
  };

  emit(olderCount, olderCenter, [6 * 60, 14 * 60], [0.4, 2.0], 0);
  emit(recentCount, recentCenter, [30, 4 * 60], [0.3, 1.8], 1000);
  // Distant secondary cell ~22 mi NNE of the track — outside the storm
  // vector's 30 mi radius bias toward the primary system but close enough
  // to render its own hull blob. Demonstrates the multi-cluster hull path.
  const secondaryCenter = offsetFromTrack(22, 30);
  emit(8, secondaryCenter, [60, 6 * 60], [0.4, 1.5], 2000);
  return strikes;
}

// Constants so HMR doesn't reseed and the wind arrows don't twitch on every
// hot reload. SSW @ 14 mph is a realistic Concord, NC race-day surface wind;
// the +1h forecast leans a touch westerly and stronger to make the two
// arrows visually distinguishable.
const MOCK_SURFACE_WIND: SurfaceWind = {
  windDeg: 200,
  windSpeed: 14,
  station: "KJQF",
};
const MOCK_FORECAST_WIND: ForecastWind = { windDeg: 230, windSpeed: 18 };

function WeatherRadarPreview() {
  const { mock } = Route.useSearch();
  const mockStrikes = useMemo(
    () =>
      mock === "lightning"
        ? generateMockStrikes({
            centerLat: CHARLOTTE_MOTOR_SPEEDWAY.lat,
            centerLng: CHARLOTTE_MOTOR_SPEEDWAY.lng,
            // Primary cell count; the generator adds a small secondary cell
            // on top to exercise the multi-cluster hull rendering.
            count: 30,
            maxAgeS: 15 * 60,
          })
        : undefined,
    [mock]
  );

  // Full-bleed — the widget fills the viewport at the same proportions it
  // renders inside the dashboard grid. The screenshot capture spec hits this
  // route directly and the result matches the in-grid appearance.
  return (
    <div className="fixed inset-0 bg-slate-900 overflow-hidden">
      <InlineErrorBoundary name="WeatherRadar">
        <Suspense fallback={<Loading />}>
          <WeatherRadar
            previewLatLng={CHARLOTTE_MOTOR_SPEEDWAY}
            mockStrikes={mockStrikes}
            mockSurfaceWind={MOCK_SURFACE_WIND}
            mockForecastWind={MOCK_FORECAST_WIND}
          />
        </Suspense>
      </InlineErrorBoundary>
    </div>
  );
}
